import React, { useCallback, useContext, useMemo } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { EnvContext } from 'States/env/envState';
import { ConfigContext } from 'States/config/configState';
import { LangContext } from 'States/lang/langState';
import { getTranslation } from 'Helpers/translations';
import useConfigSettings from 'Hooks/useConfigSettings';
import { ROUTES } from 'Router/Routes';
import InvestmentCardButtons from './InvestmentCardButtons';
import CustomCard from 'Features/Purchase/AssetCard/CustomCard';

const InvestmentCard = ({ assetConfig, dataQa }) => {
  const { env } = useContext(EnvContext);
  const {
    config: { kycTierFunctionalRestrictions }
  } = useContext(ConfigContext);
  const { lang } = useContext(LangContext);
  const history = useHistory();

  const {
    data: defaultLanguage
  } = useConfigSettings.query({
    select: useCallback(
      ({ Languages }) => {
        const defaultLanguage = Languages.find(({ IsDefault }) => IsDefault);
        return defaultLanguage;
      }, []
    )
  });

  const translatedName = useMemo(() => {
    const name = getTranslation("Name", lang, defaultLanguage.Code, assetConfig.Asset.Translations);
    const ticker = getTranslation("Ticker", lang, defaultLanguage.Code, assetConfig.Asset.Translations);
    return ticker ? `${name} (${ticker})` : name;
  }, [lang]);

  const translatedDescription = useMemo(() => getTranslation(
    "Description", lang, defaultLanguage.Code, assetConfig.Asset.Translations
  ), [lang]);

  const {
    translatedAddress,
    translatedLink
  } = useMemo(() => {
    const translatedDefinitionItems = getTranslation(
      "CustomDefinitionItems", lang, defaultLanguage.Code, assetConfig.Asset.Translations
    );
    const addressDefinitionItem = translatedDefinitionItems.find(({ Name }) => Name === "Address");
    const linkDefinitionItem = translatedDefinitionItems.find(({ Name }) => Name === "Link");
    return {
      translatedAddress: addressDefinitionItem?.Value || "",
      translatedLink: linkDefinitionItem?.Value || ""
    };
  }, [lang]);

  const openAssetInfo = () => {
    if (!translatedLink) {
      return;
    }
    window.open(translatedLink, '_blank', 'noopener,noreferrer');
  };

  const openRedemption = () => history.push({
    pathname: generatePath(
      ROUTES.redemptionForm,
      { id: assetConfig.redemptionConfig?.Id }
    ),
    state: {
      redemptionConfig: assetConfig.redemptionConfig,
      asset: assetConfig.Asset
    }
  });

  return (
    <>
      <CustomCard
        dataQa={dataQa}
        name={translatedName}
        iconUrl={`${env.BlobUrl}/${assetConfig.Icon}`}
        description={translatedDescription}
        subtitle={translatedAddress}
        buttonGroup={() =>
          InvestmentCardButtons({
            topButtonAction: openAssetInfo,
            bottomButtonAction: openRedemption,
            bottomButtonDisabled: !assetConfig.redemptionConfig,
            kycTier: kycTierFunctionalRestrictions?.visibility?.redemption || 0,
          })
        }
      />
    </>
  );
};

export default InvestmentCard;
